import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import Zone from "@insite/client-framework/Components/Zone";
import { getSession } from "@insite/client-framework/Services/SessionService";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import Hidden, { HiddenProps } from "@insite/mobius/Hidden";
import Icon from "@insite/mobius/Icon";
import { breakpointMediaQueries } from "@insite/mobius/utilities";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import React, { FC, useEffect, useRef } from "react";
import { connect } from "react-redux";
import { css } from "styled-components";

const mapStateToProps = (state: ApplicationState) => ({
    session: state.context.session,
    isAuthenticated: state.context.session.isAuthenticated,
});

interface OwnProps extends WidgetProps {}

type Props = OwnProps & ReturnType<typeof mapStateToProps>;

export interface HeaderContainerStyle {
    headerContainer?: InjectableCss;
    headerSection?: InjectableCss;
    borderBottom?: InjectableCss;
    signInborderBottom?: InjectableCss;
    logoSearchContainer?: InjectableCss;
    logo?: InjectableCss;
    search?: InjectableCss;
    login?: InjectableCss;
    loginGuest?: InjectableCss;
    myAccount?: InjectableCss;
    signInAccount?: InjectableCss;
    mainNavigation?: InjectableCss;
    cart?: InjectableCss;
    cartGuest?: InjectableCss;
    signIn?: HiddenProps;
    language?: InjectableCss;
    languageGuest?: InjectableCss;
    shiptostyle?: InjectableCss;
    logoGuest?: InjectableCss;
}

export const headerContainerStyle: HeaderContainerStyle = {

    headerContainer: {
        css: css`
            background: white;
            border-bottom: 1px solid #acacac;
            ${({ theme }) => {
                return breakpointMediaQueries(theme, [
                    css`background: black;`,
                    css`background: black;`,
                    css`background: black;`,
                    null,
                    null,
                ]);
            }}
        `,
    },

    headerSection: {
        css: css`
            ${({ theme }) => {
                return breakpointMediaQueries(
                    theme,
                    [
                        css`background-color: black;`,
                        null,
                        css`
                            display: flex;
                            align-items: center;
                            justify-content: space-between;
                            max-width: calc(100% - 40px);
                            margin: 0 auto;
                        `,
                        null,
                        null,
                    ],
                    "max",
                );
            }}
        `,
    },

    borderBottom: {
        css: css`
            background-color: black;
            ${({ theme }) => {
                return breakpointMediaQueries(theme, [
                    css`border-bottom: none; width: 100%; order: 2;`,
                    css`border-bottom: none; width: 100%; order: 2;`,
                    css`border-bottom: none; width: 100%; order: 2;`,
                    null,
                    null,
                ]);
            }}
        `,
    },

    signInborderBottom: {
        css: css`
            background-color: black;
            ${({ theme }) => {
                return breakpointMediaQueries(theme, [
                    css`border-bottom: none; width: 100%; order: 2;`,
                    css`border-bottom: none; width: 100%; order: 2;`,
                    css`border-bottom: none; width: 100%; order: 2;`,
                    null,
                    null,
                ]);
            }}
        `,
    },

    /*
     * flex-start (not space-between) — avoids gaps being distributed
     * between items. Mobile restores space-between so logo stays left
     * and cart stays right.
     */
    logoSearchContainer: {
        css: css`
            display: flex;
            align-items: center;
            justify-content: flex-start;
            max-width: 1280px;
            margin: 0 auto;
            padding: 32px 0px;
            max-height: 100px;
            ${({ theme }) => {
                return breakpointMediaQueries(
                    theme,
                    [
                        null,
                        null,
                        css`
                            justify-content: space-between;
                            max-width: 100% !important;
                            padding: 0px;
                            margin: 21px 0px;
                        `,
                        null,
                        null,
                    ],
                    "max",
                );
            }}
            @media only screen and (max-width: 1280px) {
                padding-left: 15px;
                padding-right: 15px;
            }
            @media only screen and (max-width: 992px) {
                padding-left: 0px;
                padding-right: 0px;
            }
        `,
    },

    mainNavigation: {
        css: css`
            max-width: 1440px;
            margin: 0 auto;
            ${({ theme }) => {
                return breakpointMediaQueries(
                    theme,
                    [
                        css`background-color: black;`,
                        null,
                        null,
                        null,
                        null,
                    ],
                    "max",
                );
            }}
            > div {
                max-width: 1280px;
                margin: 0 auto;
                justify-content: space-between !important;
            }
            ${({ theme }) => {
                return breakpointMediaQueries(
                    theme,
                    [
                        null,
                        null,
                        css`order: 1; margin: 0;`,
                        css``,
                        null,
                    ],
                    "max",
                );
            }}
        `,
    },

    /*
     * LOGGED-IN logo — natural size, no width %.
     * margin-right: auto not needed here; logged-in uses flex-start
     * and search fills the middle space.
     */
    logo: {
        css: css`
            flex-shrink: 0;
            padding-top: 5px;
            ${({ theme }) => {
                return breakpointMediaQueries(
                    theme,
                    [
                        null,
                        null,
                        css`width: auto; margin: 0 auto; padding-top: 0px;`,
                        null,
                        null,
                    ],
                    "max",
                );
            }}
        `,
    },

    /*
     * GUEST logo — margin-right: auto pins logo to the far left and
     * pushes Search + Cart + Language (and Sign In if present) as a
     * cluster to the right. Works whether Sign In is shown or hidden
     * via CMS — an empty loginGuest zone collapses to 0 width.
     */
    logoGuest: {
        css: css`
            flex-shrink: 0;
            padding-top: 5px;
            ${({ theme }) => {
                return breakpointMediaQueries(
                    theme,
                    [
                        null,
                        null,
                        css`width: auto; margin: 0 auto; padding-top: 0px;`,
                        null,
                        null,
                    ],
                    "max",
                );
            }}
        `,
    },

    /*
     * Fixed 420 px search. margin-left/right: auto splits the remaining
     * space equally on both sides → search centres itself between the
     * logo and the right-side action group (Sign In / Cart / Language).
     * Works for EN guest (Sign In visible) AND FR CA guest (Sign In
     * hidden via CMS) because the empty loginGuest collapses to 0 px.
     */
    search: {
        css: css`
            flex: 1 1 auto;
            margin-left: 32px;
            margin-right: 16px;
        `,
    },

    /* Authenticated login name */
    login: {
        css: css`
            flex-shrink: 0;
            margin-right: 16px;
            display: flex;
            justify-content: flex-start;
            position: relative;
        `,
    },

    /*
     * Guest Sign In link — NO fixed width.
     * When CMS hides Sign In (e.g. FR CA), the Hidden wrapper renders
     * an empty zone that collapses to 0 px — no dead whitespace block.
     */
    loginGuest: {
        css: css`
            flex-shrink: 0;
            margin-right: 24px;
            display: flex;
            justify-content: center;
            position: relative;
        `,
    },

    /*
     * ShipTo — hard max-width so the long address string never
     * overflows and squeezes Cart off-screen.
     */
    shiptostyle: {
        css: css`
            flex-shrink: 0;
            max-width: 153px;
            overflow: hidden;
            margin-right: 0;
            display: flex;
            justify-content: flex-start;
            align-items: center;
            > div {
                width: 100%;
                overflow: hidden;
            }
        `,
    },

    /* Language — authenticated */
    language: {
        css: css`
            flex-shrink: 0;
            margin-left: 8px;
            display: flex;
            justify-content: center;
            div { width: 100%; }
        `,
    },

    /* Language — guest */
    languageGuest: {
        css: css`
            flex-shrink: 0;
            margin-left: 16px;
            margin-right: 16px;
            display: flex;
            justify-content: center;
            div { width: 100%; }
        `,
    },

    /* Cart — authenticated */
    cart: {
        css: css`
            flex-shrink: 0;
            margin-left: 0;
            min-width: 100px;
            margin-right: 8px;
            max-width: 135px;
            ${({ theme }) => {
                return breakpointMediaQueries(
                    theme,
                    [
                        null,
                        null,
                        null,
                        css`min-width: unset; margin-right: 0;`,
                        css`min-width: unset; margin-right: 0;`,
                    ],
                    "max",
                );
            }}
        `,
    },

    /* Cart — guest */
    cartGuest: {
        css: css`
            flex-shrink: 0;
            min-width: 100px;
            margin-left: 8px;
            margin-right: 16px;
            ${({ theme }) => {
                return breakpointMediaQueries(
                    theme,
                    [
                        null,
                        null,
                        null,
                        css`min-width: unset; margin-right: 0;`,
                        css`min-width: unset; margin-right: 0;`,
                    ],
                    "max",
                );
            }}
        `,
    },

    signInAccount: {
        css: css`width: 12%;`,
    },

    myAccount: {
        css: css`width: 18%; padding-left: 12px;`,
    },

    signIn: {
        css: css`
            > div { display: flex; justify-content: end; }
        `,
    },
};

const styles = headerContainerStyle;

export const HeaderContainer: FC<Props> = ({ id, session, isAuthenticated }) => {
    return (
        <StyledWrapper {...styles.headerContainer}>
            <StyledWrapper {...styles.headerSection}>
                <StyledWrapper {...(!session.isAuthenticated ? styles.borderBottom : styles.signInborderBottom)}>
                    <StyledWrapper {...styles.logoSearchContainer}>
                        <StyledWrapper {...(!session.isAuthenticated ? styles.logoGuest : {...styles.logo})}>
                            <Zone contentId={id} zoneName="Logo" />
                        </StyledWrapper>
                        <Hidden below="lg" {...styles.search}>
                            <StyledWrapper>
                                <Zone contentId={id} zoneName="Search" />
                            </StyledWrapper>
                        </Hidden>
                        <Hidden below="lg" {...(!session.isAuthenticated ? styles.loginGuest : {...styles.login})}>
                            <StyledWrapper>
                                <Zone contentId={id} zoneName="whiteCapLogin" />
                            </StyledWrapper>
                        </Hidden>

                        {isAuthenticated ? (
                            <>
                                <Hidden below="lg" {...styles.shiptostyle}>
                                    <StyledWrapper>
                                        <Zone contentId={id} zoneName="shipto" />
                                    </StyledWrapper>
                                </Hidden>

                                <StyledWrapper {...styles.cart}>
                                    <Zone contentId={id} zoneName="Cart" />
                                </StyledWrapper>

                                <Hidden below="lg" {...styles.language}>
                                    <StyledWrapper>
                                        <Zone contentId={id} zoneName="language" />
                                    </StyledWrapper>
                                </Hidden>
                            </>
                        ) : (
                            <>
                                <StyledWrapper {...styles.cartGuest}>
                                    <Zone contentId={id} zoneName="Cart" />
                                </StyledWrapper>
                                <Hidden below="lg" {...styles.languageGuest}>
                                    <StyledWrapper>
                                        <Zone contentId={id} zoneName="language" />
                                    </StyledWrapper>
                                </Hidden>
                            </>
                        )}
                    </StyledWrapper>
                </StyledWrapper>
                <StyledWrapper {...styles.mainNavigation}>
                    <Zone contentId={id} zoneName="mainNavigation" />
                </StyledWrapper>
            </StyledWrapper>
        </StyledWrapper>
    );
};

const widgetModule: WidgetModule = {
    component: connect(mapStateToProps)(HeaderContainer),
    definition: {
        group: "Basic",
        displayName: "Header Container",
    },
};

export default widgetModule;
